import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { InternetConnection, InternetConnectionInsert, InternetConnectionUpdate } from '@/types/internetConnection';

interface InternetConnectionState {
  connections: InternetConnection[];
  loading: boolean;
  loadConnections: () => Promise<void>;
  addConnection: (connection: InternetConnectionInsert) => Promise<InternetConnection | null>;
  updateConnection: (id: string, updates: InternetConnectionUpdate) => Promise<void>;
  deleteConnection: (id: string) => Promise<void>;
  getConnectionById: (id: string) => InternetConnection | undefined;
  getConnectionsBySiteId: (siteId: string) => InternetConnection[];
  getConnectionsByZoneId: (zoneId: string) => InternetConnection[];
  getConnectionsBySystemId: (systemId: string) => InternetConnection[];
}

export const useInternetConnectionStore = create<InternetConnectionState>()((set, get) => ({
  connections: [],
  loading: false,

  loadConnections: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('internet_connections')
        .select('*')
        .order('label');

      if (error) throw error;
      set({ connections: (data || []) as InternetConnection[] });
    } catch (error) {
      console.error('Error loading internet connections:', error);
      toast.error('Failed to load internet connections');
    } finally {
      set({ loading: false });
    }
  },

  addConnection: async (connection) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const { data, error } = await supabase
        .from('internet_connections')
        .insert({
          ...connection,
          org_id: profile.org_id,
        })
        .select()
        .single();

      if (error) throw error;

      await get().loadConnections();
      toast.success('Internet connection created');
      return data as InternetConnection;
    } catch (error: any) {
      console.error('Error creating internet connection:', error);
      toast.error(error.message || 'Failed to create internet connection');
      return null;
    }
  },

  updateConnection: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('internet_connections')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      await get().loadConnections();
      toast.success('Internet connection updated');
    } catch (error: any) {
      console.error('Error updating internet connection:', error);
      toast.error(error.message || 'Failed to update internet connection');
    }
  },

  deleteConnection: async (id) => {
    try {
      const { error } = await supabase
        .from('internet_connections')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        connections: state.connections.filter(c => c.id !== id)
      }));
      toast.success('Internet connection deleted');
    } catch (error: any) {
      console.error('Error deleting internet connection:', error);
      toast.error(error.message || 'Failed to delete internet connection');
    }
  },

  getConnectionById: (id) => {
    return get().connections.find(c => c.id === id);
  },

  getConnectionsBySiteId: (siteId) => {
    return get().connections.filter(c => c.site_id === siteId && !c.zone_id && !c.system_id);
  },

  getConnectionsByZoneId: (zoneId) => {
    return get().connections.filter(c => c.zone_id === zoneId && !c.system_id);
  },

  getConnectionsBySystemId: (systemId) => {
    return get().connections.filter(c => c.system_id === systemId);
  },
}));
